import { Check, SlidersHorizontal } from "lucide-react"
import type { ReactNode } from "react"
import { useHaptic } from "@/shared/hooks/use-haptic"
import type { Duration } from "@/shared/lib/types"
import { cn } from "@/shared/lib/utils"
import {
	Dialog,
	DialogClose,
	DialogSheetContent,
	DialogTitle,
	DialogTrigger,
} from "@/shared/ui/dialog"

export type SortOption = "newest" | "oldest"

interface DurationFilterDialogProps {
	durations: Duration[]
	selectedDurationIds: string[]
	sortBy: SortOption
	onDurationToggle: (durationId: string) => void
	onSortChange: (value: SortOption) => void
	onClearFilters: () => void
}

const GROUP_TITLE = "text-[11px] font-semibold tracking-[0.14em] text-primary uppercase"
const ACTION =
	"inline-flex min-h-11 flex-1 items-center justify-center rounded-full px-5 text-sm font-semibold transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"

function Choice({
	active,
	onClick,
	children,
}: {
	active: boolean
	onClick: () => void
	children: ReactNode
}) {
	const { triggerSelection } = useHaptic()

	return (
		<button
			type="button"
			aria-pressed={active}
			onClick={() => {
				triggerSelection()
				onClick()
			}}
			className={cn(
				"inline-flex min-h-10 items-center gap-1.5 rounded-full px-3.5 text-sm font-medium ring-1 transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
				active
					? "bg-primary text-primary-foreground ring-primary"
					: "bg-white/[0.06] text-foreground ring-white/10",
			)}
		>
			{active && <Check className="size-3.5" />}
			{children}
		</button>
	)
}

export function DurationFilterDialog({
	durations,
	selectedDurationIds,
	sortBy,
	onDurationToggle,
	onSortChange,
	onClearFilters,
}: DurationFilterDialogProps) {
	const { triggerHaptic } = useHaptic()
	const activeCount = selectedDurationIds.length + (sortBy === "oldest" ? 1 : 0)

	return (
		<Dialog>
			<DialogTrigger asChild>
				<button
					type="button"
					aria-label="Фильтры и сортировка"
					onClick={() => triggerHaptic("light")}
					className="relative grid size-12 shrink-0 place-items-center rounded-2xl bg-card ring-1 ring-white/10 transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<SlidersHorizontal className="size-4" />
					{activeCount > 0 && (
						<span className="absolute -top-1 -right-1 grid size-5 place-items-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
							{activeCount}
						</span>
					)}
				</button>
			</DialogTrigger>

			<DialogSheetContent>
				<DialogTitle className="text-xl font-bold tracking-tight">Фильтры</DialogTitle>

				<section className="mt-5">
					<p className={GROUP_TITLE}>Сортировка</p>
					<div className="mt-2.5 flex flex-wrap gap-2">
						<Choice active={sortBy === "newest"} onClick={() => onSortChange("newest")}>
							Сначала новые
						</Choice>
						<Choice active={sortBy === "oldest"} onClick={() => onSortChange("oldest")}>
							Сначала старые
						</Choice>
					</div>
				</section>

				{durations.length > 0 && (
					<section className="mt-6">
						<p className={GROUP_TITLE}>Длительность</p>
						<div className="mt-2.5 flex flex-wrap gap-2">
							{durations.map((duration) => (
								<Choice
									key={duration.id}
									active={selectedDurationIds.includes(duration.id)}
									onClick={() => onDurationToggle(duration.id)}
								>
									{duration.name}
								</Choice>
							))}
						</div>
					</section>
				)}

				<div className="mt-7 flex gap-2">
					<button
						type="button"
						disabled={activeCount === 0}
						onClick={() => {
							triggerHaptic("light")
							onClearFilters()
							onSortChange("newest")
						}}
						className={cn(ACTION, "bg-white/[0.06] text-foreground ring-1 ring-white/10")}
					>
						Сбросить
					</button>
					<DialogClose asChild>
						<button
							type="button"
							onClick={() => triggerHaptic("medium")}
							className={cn(
								ACTION,
								"bg-primary text-primary-foreground shadow-lg shadow-primary/25",
							)}
						>
							Показать
						</button>
					</DialogClose>
				</div>
			</DialogSheetContent>
		</Dialog>
	)
}
